// <purpose>Squarified treemap layout (Bruls/Huizing/van Wijk). Pure; area ∝ value.</purpose>
// <constraints>Non-positive values/box → []. Output rects tile the box with no gaps (gaps are a render concern).</constraints>

export interface SquarifyInput { key: string; value: number; }
export interface SquarifyRect { key: string; value: number; x: number; y: number; w: number; h: number; }

interface Box { x: number; y: number; w: number; h: number; }
interface Cell { key: string; value: number; area: number; }

function worst(row: Cell[], side: number): number {
  if (row.length === 0) return Infinity;
  const sum = row.reduce((s, c) => s + c.area, 0);
  const max = Math.max(...row.map(c => c.area));
  const min = Math.min(...row.map(c => c.area));
  const s2 = sum * sum;
  const side2 = side * side;
  return Math.max((side2 * max) / s2, s2 / (side2 * min));
}

function placeRow(row: Cell[], box: Box, out: SquarifyRect[]): Box {
  const rowArea = row.reduce((s, c) => s + c.area, 0);
  if (box.w <= box.h) {
    const rowH = rowArea / box.w;
    let x = box.x;
    for (const c of row) {
      const w = c.area / rowH;
      out.push({ key: c.key, value: c.value, x, y: box.y, w, h: rowH });
      x += w;
    }
    return { x: box.x, y: box.y + rowH, w: box.w, h: box.h - rowH };
  }
  const rowW = rowArea / box.h;
  let y = box.y;
  for (const c of row) {
    const h = c.area / rowW;
    out.push({ key: c.key, value: c.value, x: box.x, y, w: rowW, h });
    y += h;
  }
  return { x: box.x + rowW, y: box.y, w: box.w - rowW, h: box.h };
}

export function squarify(items: SquarifyInput[], width: number, height: number, minAreaFraction = 0): SquarifyRect[] {
  const positive = items.filter(i => i.value > 0);
  if (positive.length === 0 || width <= 0 || height <= 0) return [];
  // Min-tile floor: clamp each weight up to a fraction of the largest, so a dominant
  // category can't squeeze the tail into unreadable slivers (legibility > strict proportionality).
  const maxV = Math.max(...positive.map(i => i.value));
  const floored = minAreaFraction > 0
    ? positive.map(i => ({ key: i.key, value: Math.max(i.value, maxV * minAreaFraction) }))
    : positive;
  const total = floored.reduce((s, i) => s + i.value, 0);
  const scale = (width * height) / total;
  let remaining: Cell[] = floored.map(i => ({ key: i.key, value: i.value, area: i.value * scale }));
  const out: SquarifyRect[] = [];
  let box: Box = { x: 0, y: 0, w: width, h: height };

  while (remaining.length > 0) {
    const side = Math.min(box.w, box.h);
    const row: Cell[] = [];
    let i = 0;
    while (i < remaining.length) {
      const candidate = [...row, remaining[i]];
      if (row.length === 0 || worst(candidate, side) <= worst(row, side)) { row.push(remaining[i]); i++; } else break;
    }
    box = placeRow(row, box, out);
    remaining = remaining.slice(row.length);
  }
  return out;
}
